import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { EASE_OUT } from "@/lib/motion";
import {
  DotSpinner, Grid, LineWobble, NewtonsCradle, Orbit, Pulsar, Ripples, Waveform,
} from "ldrs/react";
import "ldrs/react/DotSpinner.css";
import "ldrs/react/Grid.css";
import "ldrs/react/LineWobble.css";
import "ldrs/react/NewtonsCradle.css";
import "ldrs/react/Orbit.css";
import "ldrs/react/Pulsar.css";
import "ldrs/react/Ripples.css";
import "ldrs/react/Waveform.css";
import { useThemeToken } from "@/lib/theme";

/**
 * A set of eight loaders, one of which is shown for the whole of an analysis.
 * The caller picks; this component only renders the one it is handed. Which one
 * changes from run to run, so a second scan looks different from the first
 * without anything changing mid-scan.
 *
 * `paint` is not decoration — it decides which CSS rule can reach the shapes.
 * ldrs colours most loaders on ::before / ::after pseudo-elements and a few on
 * real leaf elements, and the two need different selectors (see .shiny-loader
 * in index.css). Every value here was read out of the loader's own stylesheet;
 * getting one wrong paints a translucent band across an uncoloured wrapper box
 * instead of across the shape.
 */
// Sizes are per-loader rather than one shared number: ldrs' `size` is the
// bounding box, and how much of that box each design actually fills varies a
// lot — a grid of small dots at 46 reads as half the weight of a waveform at
// the same value. These are tuned so the eight look like one family.
const LOADERS = [
  { Loader: LineWobble, paint: "pseudo", props: { size: 74, stroke: 6, speed: 1.5, bgOpacity: 0.12 } },
  { Loader: Waveform, paint: "leaf", props: { size: 46, stroke: 4, speed: 1 } },
  { Loader: NewtonsCradle, paint: "pseudo", props: { size: 74, speed: 1.4 } },
  { Loader: Grid, paint: "pseudo", props: { size: 58, speed: 1.5 } },
  { Loader: Ripples, paint: "pseudo", props: { size: 58, speed: 2 } },
  { Loader: Orbit, paint: "pseudo", props: { size: 50, speed: 1.5 } },
  { Loader: DotSpinner, paint: "pseudo", props: { size: 50, speed: 0.9 } },
  { Loader: Pulsar, paint: "pseudo", props: { size: 54, speed: 1.6 } },
];

export const LOADER_COUNT = LOADERS.length;

/** Keeps the loaders in proportion to each other at the smaller dialog size. */
function scale(props, factor) {
  const out = { ...props, size: Math.round(props.size * factor) };
  if (props.stroke) out.stroke = Math.max(2, Number((props.stroke * factor).toFixed(1)));
  return out;
}

export default function ShinyLoader({ index = 0, compact = false }) {
  const reduceMotion = useReducedMotion();
  // ldrs takes a colour, not a stylesheet value, so the token has to be
  // resolved before it gets there.
  const color = useThemeToken("--c-loader", "#0F172A");

  // No loader changes identity mid-run any more, so what this pin still buys is
  // the run-to-run variation: asked for less motion, it is the same known-calm
  // loader every time rather than whichever one the rotation had reached. The
  // sweep is off too. It still spins — that is the feedback, not the flourish.
  const step = reduceMotion ? 1 : ((index % LOADERS.length) + LOADERS.length) % LOADERS.length;
  const { Loader, paint, props } = LOADERS[step];

  return (
    // Fixed box: the eight loaders have different natural sizes, and without
    // one the panel would resize under the text every time the beat changed.
    <div
      className={`grid shrink-0 place-items-center ${compact ? "h-14 w-20" : "h-[74px] w-[86px]"}`}
      data-testid="shiny-loader"
      data-loader-step={step}
    >
      <AnimatePresence initial={false} mode="wait">
        <motion.span
          key={step}
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.86 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 1.08 }}
          transition={{ duration: 0.26, ease: EASE_OUT }}
          className="shiny-loader grid place-items-center"
          data-paint={paint}
        >
          <Loader color={color} {...(compact ? scale(props, 0.78) : props)} />
        </motion.span>
      </AnimatePresence>
    </div>
  );
}
